import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { consumePostAuthRedirect } from '../utils/postAuthRedirect';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await login(email, password);
      // Fanleague auto-join and redirect logic
      const fanleagueGroupname = sessionStorage.getItem('fanleague_groupname');
      if (fanleagueGroupname) {
        try {
          const joinResponse = await apiClient.post(`/leagues/join-fanleague/${fanleagueGroupname}`);
          
          // Clear the league cache so the joined league appears immediately in the leagues list
          sessionStorage.removeItem('leagues_data');
          sessionStorage.removeItem('user_leagues_data');
          sessionStorage.removeItem('leagues_cache_timestamp');
          
          sessionStorage.removeItem('fanleague_groupname');
          navigate(`/leagues/${joinResponse.data.league.id}`);
          return;
        } catch (err) {
          sessionStorage.removeItem('fanleague_groupname');
          navigate('/');
          return;
        }
      }
      const redirect = consumePostAuthRedirect();
      if (redirect) {
        navigate(redirect.path, { state: redirect.state });
        return;
      }
      navigate('/');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold" style={{ color: 'var(--text-primary)' }}>
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
            Or{' '}
            <Link
              to="/register"
              className="font-medium"
              style={{ color: 'var(--highlight)' }}
            >
              create a new account
            </Link>
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="border px-4 py-3 rounded" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', borderColor: '#ef4444', color: '#ef4444' }}>
              {error}
            </div>
          )}
          
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="input-field appearance-none rounded-none relative block w-full px-3 py-2 rounded-t-md focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="input-field appearance-none rounded-none relative block w-full px-3 py-2 rounded-b-md focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          <div className="text-center">
            <Link
              to="/forgot-password"
              className="font-medium"
              style={{ color: 'var(--highlight)' }}
            >
              Forgot your password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login; 